import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { avatarColor, initial, formatShortDate, formatDuration, solveRate } from '../utils/format';
import './ProfilePage.css';

const validateUsername = (name) => {
  if (/^guest_/i.test(name)) return "Username cannot start with 'guest_'";
  if (name.length < 3) return 'Username must be at least 3 characters';
  if (name.length > 20) return 'Username cannot exceed 20 characters';
  if (!/^[a-zA-Z0-9_]+$/.test(name)) return 'Username can only contain letters, numbers, and underscores';
  return null;
};

const submissionTime = (s) => {
  if (s.formattedTime && s.formattedTime !== '--:--') return s.formattedTime;
  if (typeof s.elapsedSeconds === 'number') return formatDuration(s.elapsedSeconds);
  return '-';
};

function GuessRow({ submission }) {
  const correct = submission.result === 'correct';
  const label = correct ? submission.categoryName || 'Correct' : submission.oneAway ? 'One away' : 'Incorrect';
  return (
    <li className="pf-guess">
      <span className={`pf-squares${correct ? ' correct' : ''}`} aria-hidden="true"><i /><i /><i /><i /></span>
      <span className="pf-guess-time cr-tnum">{submissionTime(submission)}</span>
      <span className="pf-guess-main">
        <b className={correct ? '' : 'cr-muted'}>{label}</b>
        {submission.words && submission.words.length > 0 && (
          <span className="pf-guess-words">{submission.words.join(', ')}</span>
        )}
      </span>
    </li>
  );
}

function HistoryRow({ game, open, onToggle }) {
  const found = Number(game.completedCategories) || 0;
  const solved = found === 4 || game.completedCategories === '4/4' || game.result === 'solved';
  const change = typeof game.ratingChange === 'number' && game.ratingChange !== 0 ? game.ratingChange : null;
  const subs = game.submissions || [];
  return (
    <div className="pf-game">
      <div className="cr-tr" role="row">
        <span className="cr-tnum" role="cell">{formatShortDate(game.date)}</span>
        <span role="cell" className={solved ? 'pf-solved' : 'cr-muted'}>{solved ? 'Solved' : 'Not solved'}</span>
        <span className="cr-r cr-tnum" role="cell">{Math.min(found, 4)}/4</span>
        <span className="cr-r cr-tnum" role="cell">{formatDuration(game.timeTaken)}</span>
        <span className={`cr-r cr-tnum ${change === null ? 'cr-faint' : change > 0 ? 'pf-up' : 'pf-down'}`} role="cell">
          {change === null ? '-' : `${change > 0 ? '+' : ''}${change}`}
        </span>
        <span className="cr-r" role="cell">
          <button type="button" className="cr-btn sm" onClick={onToggle} aria-expanded={open} disabled={subs.length === 0}>
            {open ? 'Hide' : 'Guesses'}
          </button>
        </span>
      </div>
      {open && subs.length > 0 && (
        <ol className="pf-guesses">
          {subs.map((s, i) => <GuessRow key={i} submission={s} />)}
        </ol>
      )}
    </div>
  );
}

const ProfilePage = () => {
  const navigate = useNavigate();
  const { username } = useParams();
  const { user: currentUser, token, updateUsername } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const [expandedGame, setExpandedGame] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');

  const isMe = !!currentUser && username === currentUser.username;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`/api/users/profile/${encodeURIComponent(username)}`, {
          headers: token ? { 'x-auth-token': token } : {}
        });
        if (!cancelled) setUser(response.data);
      } catch (err) {
        if (cancelled) return;
        if (err.response && err.response.status === 404) setError(`User "${username}" not found`);
        else if (err.response && err.response.status === 401) setError('Log in to view player profiles.');
        else setError(err.response?.data?.message || err.response?.data?.msg || 'Failed to load profile data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [username, token, reload]);

  useEffect(() => {
    if (user) setNewUsername(user.username);
  }, [user]);

  const handleEditToggle = () => {
    setIsEditing((v) => !v);
    setEditError('');
    setSuccess('');
    if (user) setNewUsername(user.username);
  };

  const handleUsernameChange = async (e) => {
    e.preventDefault();
    if (newUsername === user.username) {
      setIsEditing(false);
      return;
    }
    const usernameError = validateUsername(newUsername);
    if (usernameError) {
      setEditError(usernameError);
      return;
    }
    setSaving(true);
    setEditError('');
    try {
      const result = await updateUsername(newUsername);
      if (result.success) {
        setUser((prev) => ({ ...prev, username: newUsername }));
        setSuccess(result.message || 'Username updated successfully');
        setIsEditing(false);
        navigate(`/profile/${newUsername}`, { replace: true });
      } else {
        setEditError(result.message);
      }
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to update username');
    } finally {
      setSaving(false);
    }
  };

  const retry = useCallback(() => setReload((n) => n + 1), []);

  if (loading && !user) {
    return (
      <div className="cr-page pf">
        <section className="cr-card cr-card-pad"><p className="cr-muted">Loading profile...</p></section>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="cr-page pf">
        <section className="cr-card cr-card-pad pf-error">
          <p>{error || 'User not found'}</p>
          <button type="button" className="cr-btn sm" onClick={retry}>Try Again</button>
        </section>
      </div>
    );
  }

  const history = user.gameHistory || [];
  const games = user.gamesPlayed || 0;
  const solves = user.wins || 0;

  return (
    <div className="cr-page pf">
      <section className="cr-card cr-card-pad pf-head">
        {user.avatar ? (
          <img className="pf-avatar" src={user.avatar} alt="" />
        ) : (
          <span className="pf-avatar" style={{ background: avatarColor(user.username) }} aria-hidden="true">
            {initial(user.username)}
          </span>
        )}
        <div className="pf-id">
          {isEditing ? (
            <form className="pf-edit" onSubmit={handleUsernameChange}>
              <label className="cr-label" htmlFor="pf-username">Username</label>
              <div className="pf-edit-row">
                <input
                  id="pf-username"
                  className="cr-input"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
                <button type="submit" className="cr-btn solid" disabled={saving}>{saving ? 'Saving' : 'Save'}</button>
                <button type="button" className="cr-btn" onClick={handleEditToggle}>Cancel</button>
              </div>
              {editError && <p className="pf-msg error" role="alert">{editError}</p>}
            </form>
          ) : (
            <>
              <div className="pf-name-row">
                <h1 className="cr-title">{user.username}</h1>
                {isMe && <button type="button" className="cr-btn sm" onClick={handleEditToggle}>Edit</button>}
              </div>
              <p className="cr-muted cr-tnum">Rating {user.rating || 1000}</p>
              {success && <p className="pf-msg success" role="status">{success}</p>}
            </>
          )}
        </div>
      </section>

      <section className="cr-card pf-stats">
        <div className="cr-stat"><b>{user.rating || 1000}</b><span>Rating</span></div>
        <div className="cr-stat"><b>{games}</b><span>Games Played</span></div>
        <div className="cr-stat"><b>{solves}</b><span>Solves</span></div>
        <div className="cr-stat"><b>{solveRate(solves, games)}</b><span>Solve Rate</span></div>
      </section>

      <section className="cr-card pf-history">
        <div className="cr-row-head pf-history-head"><h2 className="cr-section-title">Game History</h2></div>
        {history.length === 0 ? (
          <p className="pf-empty">No games played yet</p>
        ) : (
          <div role="table" aria-label="Game history">
            <div className="cr-tr cr-th" role="row">
              <span role="columnheader">Date</span>
              <span role="columnheader">Result</span>
              <span role="columnheader" className="cr-r">Groups</span>
              <span role="columnheader" className="cr-r">Time</span>
              <span role="columnheader" className="cr-r">Rating</span>
              <span role="columnheader" className="cr-r"><span className="pf-sr">Guesses</span></span>
            </div>
            {history.map((game, index) => (
              <HistoryRow
                key={game._id || index}
                game={game}
                open={expandedGame === index}
                onToggle={() => setExpandedGame(expandedGame === index ? null : index)}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ProfilePage;
